import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useCallback, useMemo } from "react";

import {
	$agentsMdSuggestionDismissed,
	$agentsMdVersion,
	$createAgentsMd,
	$updateAgentsMd,
	AGENTS_MD_VERSION,
	type AgentsMdVersionResult,
} from "../../internal/document/agents-md.atom";

export type AgentsMdSuggestionType = "create" | "update" | null;

export const useAgentsMdSuggestion = (documentId: string | undefined) => {
	const docId = documentId ?? "";

	const versionAtom = useMemo(() => $agentsMdVersion({ documentId: docId }), [docId]);
	const versionLoadableAtom = useMemo(() => loadable(versionAtom), [versionAtom]);
	const dismissedAtom = useMemo(() => $agentsMdSuggestionDismissed(docId), [docId]);

	const versionLoadable = useAtomValue(versionLoadableAtom);
	const isDismissed = useAtomValue(dismissedAtom);
	const setDismissed = useSetAtom(dismissedAtom);
	const createAgentsMdAtom = useSetAtom($createAgentsMd);
	const updateAgentsMdAtom = useSetAtom($updateAgentsMd);

	const versionResult: AgentsMdVersionResult | undefined =
		versionLoadable.state === "hasData" ? versionLoadable.data : undefined;
	const isLoading = versionLoadable.state === "loading";

	const hasManagedSection =
		versionResult?.status === "found" ? versionResult.hasManagedSection : false;

	const suggestionType: AgentsMdSuggestionType = useMemo(() => {
		if (!documentId) {
			return null;
		}
		if (isLoading) {
			return null;
		}
		if (isDismissed) {
			return null;
		}
		if (!versionResult) {
			return null;
		}

		if (versionResult.status === "unavailable") {
			return null;
		}

		if (versionResult.status === "not-found") {
			return "create";
		}

		if (versionResult.version < AGENTS_MD_VERSION) {
			return "update";
		}

		return null;
	}, [documentId, isLoading, isDismissed, versionResult]);

	const create = useCallback(async () => {
		if (!documentId) {
			return false;
		}
		return createAgentsMdAtom({ documentId });
	}, [documentId, createAgentsMdAtom]);

	const update = useCallback(async () => {
		if (!documentId) {
			return false;
		}
		return updateAgentsMdAtom({ documentId });
	}, [documentId, updateAgentsMdAtom]);

	const dismiss = useCallback(() => {
		if (documentId) {
			setDismissed(true);
		}
	}, [documentId, setDismissed]);

	const isOutdated = useMemo(() => {
		if (!versionResult || versionResult.status !== "found") {
			return false;
		}
		return versionResult.version < AGENTS_MD_VERSION;
	}, [versionResult]);

	return {
		suggestionType,
		hasManagedSection,
		isOutdated,
		isLoading,
		create,
		update,
		dismiss,
	};
};
